/**
 * FlowHub - Environment Validator
 * Validates that all required environment variables are configured
 */

import { Alert, Button, Card, Typography } from 'antd';
import { WarningOutlined } from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;

export function EnvValidator() {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  const isConfigured = supabaseUrl && 
                       supabaseKey && 
                       supabaseUrl !== 'your-project-url.supabase.co' &&
                       supabaseKey !== 'your-anon-key-here';

  if (isConfigured) {
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-100 p-4">
      <Card className="w-full max-w-2xl shadow-xl">
        <div className="text-center mb-6">
          <WarningOutlined style={{ fontSize: 64, color: '#ff4d4f' }} />
          <Title level={2} className="mt-4">
            Configuração Necessária
          </Title>
        </div>

        <Alert
          message="Variáveis de ambiente não configuradas"
          description="O FlowHub precisa se conectar ao Supabase para funcionar."
          type="error"
          showIcon
          className="mb-6"
        />

        <div className="space-y-4">
          <div>
            <Title level={4}>📋 Passo 1: Criar Projeto no Supabase</Title>
            <Paragraph>
              1. Acesse{' '}
              <a href="https://supabase.com/dashboard" target="_blank" rel="noopener noreferrer">
                supabase.com/dashboard
              </a>
              <br />
              2. Clique em <strong>"New Project"</strong>
              <br />
              3. Preencha os dados e aguarde o provisionamento (~2 minutos)
            </Paragraph>
          </div>

          <div>
            <Title level={4}>🔑 Passo 2: Obter Credenciais</Title>
            <Paragraph>
              1. No seu projeto Supabase, vá em <strong>Settings → API</strong>
              <br />
              2. Copie:
              <br />
              - <Text code>Project URL</Text> (ex: https://abc123.supabase.co)
              <br />
              - <Text code>anon public</Text> key
            </Paragraph>
          </div>

          <div>
            <Title level={4}>⚙️ Passo 3: Configurar .env.local</Title>
            <Paragraph>
              1. Na raiz do projeto, crie/edite o arquivo <Text code>.env.local</Text>
              <br />
              2. Adicione suas credenciais:
            </Paragraph>
            <pre style={{ 
              background: '#f5f5f5', 
              padding: 16, 
              borderRadius: 8,
              overflow: 'auto'
            }}>
{`VITE_SUPABASE_URL=https://seu-projeto.supabase.co
VITE_SUPABASE_ANON_KEY=sua-chave-anon-aqui`}
            </pre>
          </div>

          <div>
            <Title level={4}>🗄️ Passo 4: Executar Migrations SQL</Title>
            <Paragraph>
              1. No Supabase, vá em <strong>SQL Editor</strong>
              <br />
              2. Copie TODO o conteúdo de{' '}
              <Text code>supabase/migrations/001_initial_schema.sql</Text>
              <br />
              3. Cole e execute no SQL Editor
              <br />
              4. Verifique em <strong>Table Editor</strong> se as 5 tabelas foram criadas
            </Paragraph>
          </div>

          <div>
            <Title level={4}>🔄 Passo 5: Reiniciar Servidor</Title>
            <Paragraph>
              Após configurar o <Text code>.env.local</Text>:
            </Paragraph>
            <pre style={{ 
              background: '#f5f5f5', 
              padding: 16, 
              borderRadius: 8 
            }}>
{`# Pare o servidor (Ctrl+C)
# Reinicie
npm run dev`}
            </pre>
          </div>
        </div>

        <div className="mt-6 text-center">
          <Button 
            type="primary" 
            size="large"
            onClick={() => window.location.reload()}
          >
            Já Configurei - Recarregar Página
          </Button>
        </div>

        <div className="mt-6">
          <Alert
            message="📚 Documentação Completa"
            description={
              <span>
                Para instruções detalhadas, consulte o arquivo{' '}
                <Text code>SETUP.md</Text> na raiz do projeto.
              </span>
            }
            type="info"
          />
        </div>
      </Card>
    </div>
  );
}
